import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import SessionRunner from "@/components/SessionRunner";
import RecordVideoModal from "@/components/RecordVideoModal";
import RecoveryPlanPanel from "@/components/RecoveryPlanPanel";
import StageRoadmap from "@/components/StageRoadmap";
import MonthStrip from "@/components/MonthStrip";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, ChevronDown, ChevronUp, Video, Play, VideoIcon,
  MessageSquare, Download, ShieldAlert, X, ChevronLeft, ChevronRight,
  CalendarDays, Calendar, CalendarRange,
} from "lucide-react";
import { Skeleton } from "@/components/Skeleton";
import { ExerciseThumb, ExerciseVideo } from "@/lib/exerciseMedia";

const VIEW_KEY = "fl_plan_view";

/* ------- Date helpers (Europe/London-safe ISO dates, no time zone math). ------- */
const iso = (d) => d.toISOString().slice(0, 10);
const parseIso = (s) => {
  const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (d, n) => {
  const c = new Date(d.getTime());
  c.setUTCDate(c.getUTCDate() + n);
  return c;
};
const startOfWeekMon = (d) => {
  // ISO week starts Monday. JS getUTCDay: Sun=0..Sat=6. Shift so Mon=0.
  const wd = (d.getUTCDay() + 6) % 7;
  return addDays(d, -wd);
};
const startOfMonth = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const endOfMonth = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));

const fmtLongDate = (d) =>
  d.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const fmtShortDate = (d) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" });
const fmtMonth = (d) =>
  d.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });

/* ================================================================
 * PatientPlan — Day / Week / Month tabs backed by /api/my-plan.
 * Scheduling is entirely backend-driven; this component only groups
 * the returned days for presentation.
 * ================================================================ */
export default function PatientPlan() {
  const { t } = useTranslation();
  const nav = useNavigate();

  // Programme-level context (goal, phase, title, banner)
  const [prog, setProg] = useState(null);
  const [redZone, setRedZone] = useState(null);
  const [stages, setStages] = useState(null); // S1–S6 rehab stage track (null until loaded)
  const [selectedStage, setSelectedStage] = useState(null); // active S1–S6 node (controlled)
  const [periCurrent, setPeriCurrent] = useState(null); // published periodisation context

  // Scheduled plan data
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "day"; }
    catch { return "day"; }
  });
  const [anchor, setAnchor] = useState(() => {
    // "anchor" is the ISO date the current view centres on. Defaults to today
    // in the patient's local timezone but stored as pure ISO date.
    const now = new Date();
    return iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
  });
  const [planData, setPlanData] = useState(null); // { from, to, timezone, programme, days: [...] }
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [expandedDate, setExpandedDate] = useState(null); // for Week / Month drill-down
  // iter105 · Post-review-P2 · prevents rapid double-tap racing on the same
  // exercise from clobbering the newer optimistic state.
  const [togglingIds, setTogglingIds] = useState(() => new Set());

  // Existing per-exercise interactions
  const [open, setOpen] = useState({});
  const [running, setRunning] = useState(false);
  const [recording, setRecording] = useState(null);
  const [consulting, setConsulting] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [demoEx, setDemoEx] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* noop */ }
    setExpandedDate(null);
  }, [view]);

  /* ---- Compute the window sent to /api/my-plan for the current view. ---- */
  const [from, to] = useMemo(() => {
    const a = parseIso(anchor);
    if (view === "week") {
      const start = startOfWeekMon(a);
      return [iso(start), iso(addDays(start, 6))];
    }
    if (view === "month") {
      const start = startOfMonth(a);
      const end = endOfMonth(a);
      return [iso(start), iso(end)];
    }
    return [anchor, anchor];
  }, [view, anchor]);

  /* ---- Loaders ---- */
  const loadProg = async () => {
    try {
      const [p, rz] = await Promise.all([
        api.get("/programmes/mine"),
        api.get("/coach/red-zone").catch(() => ({ data: { locked: false } })),
      ]);
      setProg(p.data);
      setRedZone(rz.data?.locked ? { message: rz.data.message } : null);
    } catch (e) {
      // programmes/mine returns null when no programme — swallow.
    }
  };

  // Monotonic request id so a slower earlier response can never overwrite the
  // result of the current (stage-filtered) request — fixes the initial-load race.
  const planReqId = useRef(0);
  const loadPlan = async () => {
    const reqId = ++planReqId.current;
    setPlanLoading(true);
    setPlanError(null);
    try {
      const params = { from, to };
      if (stages?.confirmed && selectedStage) params.stage_key = selectedStage;
      const res = await api.get(`/my-plan`, { params });
      if (reqId !== planReqId.current) return; // stale response — discard
      setPlanData(res.data);
    } catch (e) {
      if (reqId !== planReqId.current) return;
      setPlanError(e?.response?.data?.detail || "Could not load plan.");
      setPlanData(null);
    } finally {
      if (reqId === planReqId.current) setPlanLoading(false);
    }
  };

  const loadStages = async () => {
    try {
      const r = await api.get("/rehab-stages/mine");
      setStages(r.data || { confirmed: false, stages: [] });
    } catch { setStages({ confirmed: false, stages: [] }); }
  };

  const loadPeriodisation = async () => {
    try {
      const r = await api.get("/periodisation/my-current");
      setPeriCurrent(r.data?.published ? r.data : null);
    } catch { setPeriCurrent(null); }
  };

  useEffect(() => { loadProg(); loadStages(); loadPeriodisation(); }, []);
  useEffect(() => {
    // Default the active stage to the clinician's current stage once the
    // confirmed S1–S6 track loads (keeps any prior patient selection).
    if (stages?.confirmed) {
      setSelectedStage((prev) => prev || stages.current_stage || stages.stages?.[0]?.key || null);
    }
  }, [stages]);
  useEffect(() => {
    // Hold back loading until the stage track is known. When stages are
    // confirmed we must wait for the default stage to be chosen, otherwise an
    // unfiltered request can land last and clobber the stage-filtered plan.
    if (stages === null) return;
    if (stages.confirmed && !selectedStage) return;
    loadPlan();
    /* eslint-disable-next-line */
  }, [from, to, selectedStage, stages?.confirmed, stages]);

  /* ---- Actions ---- */
  const goPrev = () => {
    const a = parseIso(anchor);
    if (view === "day") setAnchor(iso(addDays(a, -1)));
    else if (view === "week") setAnchor(iso(addDays(a, -7)));
    else setAnchor(iso(addDays(startOfMonth(a), -1))); // last day of prev month
  };
  const goNext = () => {
    const a = parseIso(anchor);
    if (view === "day") setAnchor(iso(addDays(a, 1)));
    else if (view === "week") setAnchor(iso(addDays(a, 7)));
    else setAnchor(iso(addDays(endOfMonth(a), 1))); // first day of next month
  };
  const goToday = () => {
    const now = new Date();
    setAnchor(iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))));
  };

  const toggleComplete = async (dayIso, ex) => {
    const key = `${dayIso}:${ex.programme_exercise_id}`;
    if (togglingIds.has(key)) return;  // ignore rapid double-taps
    setTogglingIds((s) => new Set(s).add(key));
    // Optimistic update — rolled back on error.
    const next = !ex.completed;
    setPlanData((pd) => pd && ({
      ...pd,
      days: pd.days.map((d) => d.date !== dayIso ? d : ({
        ...d,
        exercises: d.exercises.map((e) =>
          e.programme_exercise_id === ex.programme_exercise_id ? { ...e, completed: next } : e
        ),
      })),
    }));
    try {
      await api.post("/my-plan/complete", {
        programme_exercise_ids: ex.programme_exercise_ids || [ex.programme_exercise_id],
        scheduled_date: dayIso,
        completed: next,
      });
    } catch (e) {
      // rollback to prior state (not `!next` — a stale response can't
      // clobber a newer optimistic value that way).
      setPlanData((pd) => pd && ({
        ...pd,
        days: pd.days.map((d) => d.date !== dayIso ? d : ({
          ...d,
          exercises: d.exercises.map((x) =>
            x.programme_exercise_id === ex.programme_exercise_id ? { ...x, completed: ex.completed } : x
          ),
        })),
      }));
      toast.error(e?.response?.data?.detail || "Could not save completion.");
    } finally {
      setTogglingIds((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };

  const downloadPdf = async (range) => {
    setDownloading(true);
    try {
      const res = await api.get(`/programmes/mine/pdf`, {
        params: { range, date: anchor },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = range === "week" ? from : anchor.slice(0, 7);
      a.download = `functional-lab-plan-${range}-${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    } finally { setDownloading(false); }
  };

  const askClinician = async (e) => {
    setConsulting(e.programme_exercise_id);
    try {
      const { data: contacts } = await api.get("/messages/contacts");
      const clinician = (contacts || []).find((c) => c.role === "clinician")
                        || (contacts || [])[0];
      if (!clinician?.id) {
        toast.error("No clinician assigned yet — please contact the clinic.");
        return;
      }
      const draft = `Hi ${clinician.name?.split(" ")[0] || "there"} — quick question about "${e.name || "an exercise"}" from my plan: `;
      nav(`/app/messages?to=${encodeURIComponent(clinician.id)}&prefill=${encodeURIComponent(draft)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open messages.");
    } finally { setConsulting(null); }
  };

  /* ---- Derived helpers ---- */
  const daysByIso = useMemo(() => {
    const m = new Map();
    (planData?.days || []).forEach((d) => m.set(d.date, d));
    return m;
  }, [planData]);

  const rangeLabel = useMemo(() => {
    const a = parseIso(anchor);
    if (view === "day") return fmtLongDate(a);
    if (view === "week") {
      const s = startOfWeekMon(a); const e = addDays(s, 6);
      return `Week of ${fmtShortDate(s)} – ${fmtShortDate(e)}`;
    }
    return fmtMonth(a);
  }, [view, anchor]);

  const totalToday = useMemo(() => {
    const day = daysByIso.get(anchor);
    if (!day) return { total: 0, done: 0 };
    return {
      total: day.exercises.length,
      done: day.exercises.filter((e) => e.completed).length,
    };
  }, [daysByIso, anchor]);

  /* ---- Unified-container + stage-gating derived state ---- */
  const unified = !!stages?.confirmed;
  const selectedStageObj = useMemo(
    () => (stages?.stages || []).find((s) => s.key === selectedStage) || null,
    [stages, selectedStage]
  );
  const stageLocked = unified && selectedStageObj && selectedStageObj.status === "not_started";
  const showingFallback = unified && !!planData?.stage_key && planData?.stage_matched === false;

  // The Day/Week/Month schedule — shared between the unified (stage) layout and
  // the legacy layout so the body JSX is never duplicated.
  const scheduleSection = (
    <>
      <div className="mb-4" data-testid="plan-schedule-intro">
        <div className="fl-label text-xs text-zinc-400">
          {unified
            ? (selectedStageObj ? `${selectedStageObj.key} · ${selectedStageObj.title}` : "Exercises for this stage")
            : "Your schedule"}
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          {stageLocked
            ? "This stage is upcoming. Your clinician will unlock it when you’re ready."
            : showingFallback
              ? "No programme is linked to this stage yet — showing your current overall plan."
              : "Work through your Day, Week or Month plan below."}
        </p>
      </div>

      {stageLocked ? (
        <div className="rounded-xl border border-white/10 p-8 text-center bg-white/[0.02]" data-testid="plan-stage-locked">
          <div className="fl-heading text-lg">Upcoming stage</div>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            Exercises for {selectedStageObj?.key} aren’t available yet. Your clinician will open this stage during your rehabilitation.
          </p>
        </div>
      ) : (
        <>
          {/* -------------------- View tabs + navigation -------------------- */}
          <div className={unified ? "flex items-center justify-between gap-3 flex-wrap mb-4" : "fl-card p-4 mb-6 flex items-center justify-between gap-3 flex-wrap"} data-testid="plan-view-bar">
            <div className="flex items-center gap-1 rounded-full p-1" style={{ background: "var(--fl-panel-2, rgba(255,255,255,0.04))" }}>
              {[
                ["day", "Day", CalendarDays],
                ["week", "Week", CalendarRange],
                ["month", "Month", Calendar],
              ].map(([k, label, Icon]) => (
                <button
                  key={k}
                  onClick={() => setView(k)}
                  data-testid={`plan-view-${k}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition ${
                    view === k
                      ? "bg-[#f47a20] text-black"
                      : "text-zinc-400 hover:text-zinc-100"
                  }`}
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {view !== "month" && (
                <>
                  <button onClick={goPrev} className="fl-btn-ghost text-xs" data-testid="plan-nav-prev"><ChevronLeft size={14} /></button>
                  <div className="text-sm font-semibold text-zinc-200 min-w-[10ch] text-center" data-testid="plan-range-label">
                    {rangeLabel}
                  </div>
                  <button onClick={goNext} className="fl-btn-ghost text-xs" data-testid="plan-nav-next"><ChevronRight size={14} /></button>
                </>
              )}
              <button onClick={goToday} className="fl-btn-ghost text-xs" data-testid="plan-nav-today">Today</button>
            </div>
          </div>

          {/* -------------------- Loading / error / empty -------------------- */}
          {planLoading && (
            <div className="space-y-3" data-testid="plan-loading">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="fl-card fl-card-elevated p-5 flex items-center gap-4">
                  <Skeleton className="w-20 h-20 rounded-lg" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-48 rounded-md" />
                    <Skeleton className="h-3 w-32 mt-2 rounded" />
                    <Skeleton className="h-3 w-64 mt-2 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!planLoading && planError && (
            <div className="fl-card p-6 text-center" data-testid="plan-error">
              <div className="fl-label text-red-300">Couldn't load your plan</div>
              <p className="text-sm text-zinc-400 mt-2">{planError}</p>
              <button onClick={loadPlan} className="fl-btn text-xs mt-3">Retry</button>
            </div>
          )}
          {!planLoading && !planError && !planData?.programme && (
            <div className="fl-card p-8 text-center" data-testid="plan-empty">
              <div className="fl-heading text-lg">No active programme yet</div>
              <p className="text-sm text-zinc-400 mt-2">
                Your clinician will assign a rehabilitation plan after your first session.
              </p>
            </div>
          )}

          {/* -------------------- Day / Week / Month bodies -------------------- */}
          {!planLoading && !planError && planData?.programme && view === "day" && (
            <DayView
              date={anchor}
              day={daysByIso.get(anchor)}
              totalToday={totalToday}
              form={{}}
              open={open}
              setOpen={setOpen}
              onComplete={toggleComplete}
              togglingIds={togglingIds}
              onDemo={(ex) => setDemoEx(ex)}
              onRecord={(ex) => setRecording(ex)}
              onAsk={askClinician}
              consulting={consulting}
            />
          )}
          {!planLoading && !planError && planData?.programme && view === "week" && (
            <WeekView
              anchor={anchor}
              days={planData.days}
              onOpenDay={(d) => { setExpandedDate(d); setAnchor(d); setView("day"); }}
            />
          )}
          {!planLoading && !planError && planData?.programme && view === "month" && (
            <div className="space-y-4" data-testid="plan-month-view">
              <MonthStrip
                monthAnchor={iso(startOfMonth(parseIso(anchor)))}
                onMonthChange={(firstIso) => setAnchor(firstIso)}
                value={anchor}
                onChange={(d) => setAnchor(d)}
                getDayMeta={(isoDay) => {
                  const day = daysByIso.get(isoDay);
                  const total = day?.exercises?.length || 0;
                  if (!total) return null;
                  const done = day.exercises.filter((e) => e.completed).length;
                  return { count: total, dots: [done === total ? "#22C55E" : "#14B8A6"] };
                }}
                accent="#14B8A6"
                testid="plan-month-strip"
              />
              <DayView
                date={anchor}
                day={daysByIso.get(anchor)}
                totalToday={totalToday}
                open={open}
                setOpen={setOpen}
                onComplete={toggleComplete}
                togglingIds={togglingIds}
                onDemo={(ex) => setDemoEx(ex)}
                onRecord={(ex) => setRecording(ex)}
                onAsk={askClinician}
                consulting={consulting}
              />
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <DashLayout>
      {running && prog && (
        <SessionRunner
          programme={prog}
          onClose={() => setRunning(false)}
          onComplete={() => { setRunning(false); loadPlan(); loadProg(); }}
        />
      )}
      {recording && (
        <RecordVideoModal
          exercise={recording}
          onClose={() => setRecording(null)}
          onUploaded={() => { loadPlan(); loadProg(); }}
        />
      )}

      {/* -------------------- Header -------------------- */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="fl-heading text-3xl">{planData?.programme?.title || prog?.title || t("pages.plan.h1Fallback")}</h1>
          <div className="mt-2 text-zinc-400">{prog?.goal}</div>
          {(prog?.last_ai_mutation_at || prog?.created_at) && (
            <div className="mt-2 text-[11px] inline-flex items-center gap-2 px-2 py-1 rounded-full"
                 style={{ background: "color-mix(in oklab, #22C55E 12%, transparent)", color: "#86EFAC", border: "1px solid color-mix(in oklab, #22C55E 30%, transparent)" }}
                 data-testid="plan-last-updated">
              <CheckCircle2 size={11} />
              Updated {new Date(prog.last_ai_mutation_at || prog.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}{prog?.generated_source ? " · personalised by you" : " · approved by clinician"}
            </div>
          )}
        </div>
        {(prog?.exercises?.length > 0 || planData?.days?.length > 0) && (
          <div className="flex flex-wrap gap-2 items-center">
            <button
              className="fl-btn text-base px-5 py-3"
              onClick={() => setRunning(true)}
              data-testid="start-session-btn"
            >
              <Play size={16} /> Start session
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => downloadPdf("week")}
                disabled={downloading}
                className="fl-btn-ghost text-xs"
                title="Download the visible week as PDF"
                data-testid="plan-download-week"
              >
                <Download size={12} /> Weekly PDF
              </button>
              <button
                onClick={() => downloadPdf("month")}
                disabled={downloading}
                className="fl-btn-ghost text-xs"
                title="Download the visible month as PDF"
                data-testid="plan-download-month"
              >
                <Download size={12} /> Monthly PDF
              </button>
            </div>
          </div>
        )}
      </div>

      {redZone && (
        <div
          className="fl-card p-5 mb-6 border"
          style={{ borderColor: "rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)" }}
          data-testid="plan-red-zone-banner"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-red-400 shrink-0 mt-0.5" size={20} />
            <div>
              <div className="fl-label text-red-300">Coach paused · clinician alerted</div>
              <p className="text-sm text-zinc-200 mt-1 leading-relaxed">{redZone.message}</p>
              <p className="text-[11px] text-zinc-400 mt-2">
                Please rest and keep logging any symptom changes until your clinician clears the pause.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- Recovery roadmap + schedule --------------------
          When the clinician has confirmed the S1–S6 stage structure, the
          roadmap, the selected-stage summary and the Day/Week/Month schedule
          live inside ONE unified container. Selecting a stage node loads only
          that stage's programme (falling back to the overall plan when the
          stage has no linked programme). Otherwise the legacy layout is kept
          unchanged for existing patients. */}
      {unified ? (
        <div className="fl-card fl-card-elevated p-4 sm:p-6 mb-6" data-testid="plan-unified-container">
          {periCurrent?.microcycle && (
            <div className="mb-3 pb-3 border-b border-white/10" data-testid="plan-periodisation-strip">
              <div className="text-[10px] uppercase tracking-widest text-[#f47a20]">Your current block</div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span className="fl-heading text-base sm:text-lg">{periCurrent.microcycle.name}</span>
                <span className="text-xs text-zinc-400">{fmtShortDate(parseIso(periCurrent.microcycle.start))} – {fmtShortDate(parseIso(periCurrent.microcycle.end))}</span>
                {periCurrent.microcycle.training_days != null && <span className="text-xs text-zinc-500">· {periCurrent.microcycle.training_days} training days</span>}
              </div>
              {(periCurrent.phase?.name || periCurrent.clinical_phase_title) && (
                <div className="text-xs text-zinc-400 mt-1">
                  {periCurrent.phase?.name ? `Phase: ${periCurrent.phase.name}` : ""}
                  {periCurrent.clinical_phase_title ? ` · Clinical phase: ${periCurrent.clinical_phase_title}` : ""}
                  {periCurrent.phase?.objective ? ` — ${periCurrent.phase.objective}` : ""}
                </div>
              )}
            </div>
          )}
          <StageRoadmap
            embedded
            stages={stages.stages || []}
            currentStage={stages.current_stage}
            selectedKey={selectedStage}
            onSelect={setSelectedStage}
          />
          <div className="mt-6 pt-6 border-t border-white/10">
            {scheduleSection}
          </div>
        </div>
      ) : (
        <>
          <RecoveryPlanPanel />
          {scheduleSection}
        </>
      )}

      {demoEx && <DemoModal exercise={demoEx} onClose={() => setDemoEx(null)} />}
    </DashLayout>
  );
}

/* ================================================================
 * DayView — full detail cards for a single date's scheduled exercises.
 * ================================================================ */
function DayView({ date, day, totalToday, open, setOpen, onComplete, togglingIds, onDemo, onRecord, onAsk, consulting }) {
  if (!day || day.exercises.length === 0) {
    return (
      <div className="fl-card p-8 text-center" data-testid="plan-day-empty">
        <div className="fl-heading text-lg">Rest day</div>
        <p className="text-sm text-zinc-400 mt-2">Nothing scheduled for this day. Enjoy the recovery.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="fl-label text-[#f47a20]">
          {totalToday.done} of {totalToday.total} completed
        </div>
      </div>
      <div className="space-y-3">
        {day.exercises.map((e, idx) => {
          const isOpen = open[e.programme_exercise_id];
          const asExercise = {
            id: e.exercise_id,
            name: e.name,
            video_url: e.video?.url,
            video_public_id: e.video?.public_id,
            thumbnail_url: e.video?.thumbnail_url,
            instructions: e.description,
          };
          return (
            <div
              key={e.programme_exercise_id}
              className={`fl-card fl-card-elevated overflow-hidden ${e.completed ? "opacity-80" : ""}`}
              data-testid={`plan-ex-${idx}`}
            >
              <div className="w-full p-5 flex items-center gap-4 text-left">
                <button
                  onClick={() => onComplete(date, e)}
                  disabled={togglingIds?.has(`${date}:${e.programme_exercise_id}`)}
                  className="shrink-0 disabled:opacity-60"
                  aria-label={e.completed ? "Mark as not done" : "Mark as complete"}
                  data-testid={`plan-complete-${idx}`}
                  title={e.completed ? "Mark as not done" : "Mark as complete"}
                >
                  {e.completed
                    ? <CheckCircle2 size={26} className="text-emerald-400" />
                    : <Circle size={26} className="text-zinc-500 hover:text-zinc-200" />}
                </button>
                <button
                  onClick={() => setOpen({ ...open, [e.programme_exercise_id]: !isOpen })}
                  className="flex-1 flex items-center gap-4 text-left"
                >
                  <ExerciseThumb ex={asExercise} className="w-20 h-20 rounded" />
                  <div className="flex-1">
                    <div className={`fl-heading text-lg ${e.completed ? "line-through text-zinc-500" : ""}`}>{e.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {e.sets ? `${e.sets} × ${e.reps || "?"}` : `${e.duration_seconds || "?"}s hold`}
                      {e.side && e.side !== "both" ? ` · ${e.side}` : ""}
                    </div>
                    {e.clinician_notes && <div className="text-xs text-zinc-400 mt-1">{e.clinician_notes}</div>}
                  </div>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-white/10 p-5 space-y-4">
                  {e.description && <div className="text-sm text-zinc-300">{e.description}</div>}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onDemo(asExercise)}
                      className="fl-btn-ghost text-xs"
                      title="Watch the demonstration your clinician recorded"
                      disabled={!e.video?.url}
                      data-testid={`watch-demo-btn-${idx}`}
                    >
                      <Video size={14} /> Watch demo
                    </button>
                    <button
                      onClick={() => onRecord({
                        id: e.programme_exercise_id,
                        exercise: asExercise,
                      })}
                      className="fl-btn-ghost text-xs"
                      data-testid={`record-btn-${idx}`}
                    >
                      <VideoIcon size={14} /> Record my technique
                    </button>
                    <button
                      onClick={() => onAsk(e)}
                      disabled={consulting === e.programme_exercise_id}
                      className="fl-btn-ghost text-xs disabled:opacity-50"
                      data-testid={`consult-btn-${idx}`}
                    >
                      <MessageSquare size={14} /> {consulting === e.programme_exercise_id ? "Opening…" : "Ask a clinician"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================
 * WeekView — 7-day summary strip. Tap a day to open the Day view.
 * ================================================================ */
function WeekView({ anchor, days, onOpenDay }) {
  const start = startOfWeekMon(parseIso(anchor));
  const dayList = Array.from({ length: 7 }, (_, i) => iso(addDays(start, i)));
  const byIso = new Map((days || []).map((d) => [d.date, d]));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="plan-week-grid">
      {dayList.map((d) => {
        const day = byIso.get(d);
        const done = (day?.exercises || []).filter((e) => e.completed).length;
        const total = day?.exercises?.length || 0;
        const dt = parseIso(d);
        const isToday = d === iso(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())));
        return (
          <button
            key={d}
            onClick={() => onOpenDay(d)}
            className={`fl-card p-3 text-left transition hover:border-[#f47a20]/40 ${isToday ? "ring-1 ring-[#f47a20]/50" : ""}`}
            data-testid={`plan-week-day-${d}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {dt.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" })}
            </div>
            <div className="fl-heading text-xl mt-1">{dt.getUTCDate()}</div>
            <div className="text-[11px] text-zinc-500 mt-1">{dt.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })}</div>
            <div className="mt-3 flex items-center justify-between">
              {total > 0 ? (
                <>
                  <span className="text-xs text-zinc-300">{total} ex</span>
                  <span className={`text-[11px] font-semibold ${done === total ? "text-emerald-400" : "text-[#f47a20]"}`}>
                    {done}/{total}
                  </span>
                </>
              ) : (
                <span className="text-[11px] text-zinc-600">Rest</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ================================================================
 * DemoModal — unchanged copy of the pre-existing modal.
 * ================================================================ */
function DemoModal({ exercise, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="demo-modal"
    >
      <div
        role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="fl-card-elevated w-full max-w-3xl overflow-hidden max-h-[92vh] flex flex-col"
        style={{ background: "var(--fl-bg-2)" }}
      >
        <div className="flex items-start justify-between gap-4 p-4 sm:p-5 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div className="min-w-0">
            <div className="fl-label text-[#f47a20]">Demonstration</div>
            <div className="fl-heading text-xl sm:text-2xl mt-1 truncate">{exercise?.name}</div>
          </div>
          <button onClick={onClose} aria-label="Close demo" className="text-zinc-400 hover:text-white -mr-1" data-testid="demo-modal-close">
            <X size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="bg-black">
            <ExerciseVideo ex={exercise} className="max-h-[65vh]" />
          </div>
          {exercise?.instructions && (
            <div className="p-4 sm:p-5 space-y-3">
              <p className="text-sm text-zinc-300 leading-relaxed">{exercise.instructions}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
